import React from "react";
import { Route } from "react-router-dom";

// Note: we're duplicating this dependency from `@kazoohr/confetti` to avoid
// introducing coupling between Confetti and this package. (Fewer dependencies
// makes most things faster!)
import loadable from "@loadable/component";

/**
 * Creates a route that, when triggered, loads its component async. Webpack (and
 * most other bundlers) treat these as split points, and will automatically
 * chunk them out into their own pieces for loading when invoked.
 *
 * @example
 * ```ts
 * import { AsyncRoute } from "@kazoohr/router";
 *
 * // Works like a normal route!
 * <AsyncRoute
 *   path="/admin/check-ins"
 *   module={() => import("@kazoohr/check-ins")}
 *   component={(module) => module["CheckInsAdminRoutes"]}
 * />
 * ```
 */
export function AsyncRoute<Module extends Promise<any>>({
  module,
  component,
  fallback,
  exact,
  path,
}: {
  path?: string | string[];
  exact?: boolean;
  module: () => Module;
  component: (module: Awaited<Module>) => React.ComponentType<any>;
  /**
   * Displayed while the component is loading
   */
  fallback?: JSX.Element;
}) {
  const Component = React.useMemo(() => {
    return loadable(module, {
      resolveComponent: component,
      fallback,
    });
  }, []);

  return (
    <Route key={String(path)} path={path} exact={exact} component={Component} />
  );
}
